import z from "zod";
import {ReactElement} from "react";

/**
 *   Enums
 **/
export enum ConclusionKeys {
    NotApplicable = "NOT_APPLICABLE",
    Inconclusive = "Inconclusive", //Use this option to catch flows that result in no conclusion or rating
    Green = "END_GREEN",
    Red = "END_RED",
    Amber = "END_AMBER",
}
export const zodConclusionKeys = z.nativeEnum(ConclusionKeys);

export enum EndStatusEnum {
    IN_PROGRESS = "IN_PROGRESS",
    COMPLETED = "COMPLETED",
    SKIPPED = "SKIPPED",
    NOT_STARTED = "NOT_STARTED",
}

export enum QuestionTypeEnum {
    YesNo = "YesNo",
    YesNoDontKnow = "YesNoDontKnow",
    YesNA = "YesNA",
    FreeText = "FreeText",
    Number = "Number",
    DropDown = "DropDown"
}
export const zodQuestionTypes = z.nativeEnum(QuestionTypeEnum);

export enum YesNoEnum {
    YES = "yes",
    NO = "no"
}
export const zodYesNoType = z.nativeEnum(YesNoEnum);
export enum ButtonOptionsEnum {
    YES = "yes",
    NO = "no",
    NA = "na",
    DONT_KNOW = "dont_know",
}

export const zodButtonOptions = z.nativeEnum(ButtonOptionsEnum);

export const ErrorResponse = z.object({
    errorMessage: z.string(),
    correlationId: z.string(),
    statusCode: z.string(),
});
export const ErrorMessage = z.object({
    id: z.string().optional(),
    message: z.string(),
    title: z.string().optional(),
    statusCode: z.string().optional(),
    correlationId: z.string().optional(),
});
export type ErrorResponseType = z.infer<typeof ErrorResponse>;
export type ErrorMessageType = z.infer<typeof ErrorMessage>;

export enum FindingAreasEnum {
    EmploymentTaxCompliance = "Compliance",
    ApprenticeshipLevy = "AppLevy",
    Benefits = "Benefits",
    Expenses = "Expenses",
    PayeSettlementAgreement = "PSA",
    Loans = "Loans",
    CoronavirusJobRetentionScheme = "CJRS",
    NonResidentDirectors = "NRD",
    OffPayrollWorkersPsc = "OPW1",
    OffPayrollWorkersSelfEmployedWorkers = "OPW2",
    OffPayrollWorkersAgencyEngagedWorkers = "OPW3",
    TerminationPayments = "TP",
    ConstructionIndustryScheme = "CIS",
    NationalMinimumWage = "NMW",
    Payroll = "Payroll",
    GlobalMobility = "GM",
    FollowUp = "FollowUp",
}

const FindingAreasDisplayText = {
    [FindingAreasEnum.EmploymentTaxCompliance]: "Employment Tax Compliance",
    [FindingAreasEnum.ApprenticeshipLevy]: "Apprenticeship Levy",
    [FindingAreasEnum.Benefits]: "Benefits",
    [FindingAreasEnum.Expenses]: "Expenses",
    [FindingAreasEnum.PayeSettlementAgreement]: "PAYE Settlement Agreement (\"PSA\")",
    [FindingAreasEnum.Loans]: "Loans",
    [FindingAreasEnum.CoronavirusJobRetentionScheme]: "Coronavirus Job Retention Scheme",
    [FindingAreasEnum.NonResidentDirectors]: "Non-Resident Directors",
    [FindingAreasEnum.OffPayrollWorkersPsc]: "Off-Payroll Workers - Personal Service Companies (\"PSC\")",
    [FindingAreasEnum.OffPayrollWorkersSelfEmployedWorkers]: "Off-Payroll Workers (Self-Employed)",
    [FindingAreasEnum.OffPayrollWorkersAgencyEngagedWorkers]: "Off-Payroll Workers (Agency-Engaged)",
    [FindingAreasEnum.TerminationPayments]: "Termination Payments",
    [FindingAreasEnum.ConstructionIndustryScheme]: "Construction Industry Scheme (\"CIS\")",
    [FindingAreasEnum.NationalMinimumWage]: "National Minimum Wage",
    [FindingAreasEnum.Payroll]: "Payroll",
    [FindingAreasEnum.GlobalMobility]: "Global Mobility",
    [FindingAreasEnum.FollowUp]: "Follow-up Questions",
};

const FindingAreasSubmissionText: { [key: string]: string } = {
    [FindingAreasEnum.EmploymentTaxCompliance]: "EmploymentTaxCompliance",
    [FindingAreasEnum.ApprenticeshipLevy]: "ApprenticeshipLevy",
    [FindingAreasEnum.Benefits]: "Benefits",
    [FindingAreasEnum.Expenses]: "Expenses",
    [FindingAreasEnum.PayeSettlementAgreement]: "PayeSettlementAgreement",
    [FindingAreasEnum.Loans]: "Loans",
    [FindingAreasEnum.CoronavirusJobRetentionScheme]: "CoronavirusJobRetentionScheme",
    [FindingAreasEnum.NonResidentDirectors]: "NonResidentDirectors",
    [FindingAreasEnum.OffPayrollWorkersPsc]: "OffPayrollWorkersPsc",
    [FindingAreasEnum.OffPayrollWorkersSelfEmployedWorkers]: "OffPayrollWorkersSelfEmployedWorkers",
    [FindingAreasEnum.OffPayrollWorkersAgencyEngagedWorkers]: "OffPayrollWorkersAgencyEngagedWorkers",
    [FindingAreasEnum.TerminationPayments]: "TerminationPayments",
    [FindingAreasEnum.ConstructionIndustryScheme]: "ConstructionIndustryScheme",
    [FindingAreasEnum.NationalMinimumWage]: "NationalMinimumWage",
    [FindingAreasEnum.Payroll]: "Payroll",
    [FindingAreasEnum.GlobalMobility]: "GlobalMobility",
    [FindingAreasEnum.FollowUp]: "FollowUp",
}

export enum StepActionType {
    COMPLETE = "complete",
    SKIP = "skip"
}

export const getFindingAreaFullNameFromAbbreviation = (abbreviation: string): string | undefined => {
    const entry = Object.entries(FindingAreasEnum).find(
        ([, value]) => value === abbreviation
    );

    return entry ? entry[0] : undefined;
};

export const getFindingAreaDisplayText = (area: FindingAreasEnum): string => {
    return FindingAreasDisplayText[area] || "Unknown Area";
};

export const getFindingAreaSubmissionText = (area: FindingAreasEnum): string => {
    return FindingAreasSubmissionText[area] || "Unknown Area";
};

export const getFindingAreasEnumOrdinal = (area: FindingAreasEnum): number => {
    return Object.keys(FindingAreasEnum).indexOf(area);
}

/**
 *   Types
 **/
export type Result = {
    conclusionKeys: ConclusionKeys;
};

export type AnswerOptions = Record<string, Question | Result | string>;

export type QuestionTextType = ReactElement | string;

export type Question = {
    id: string;
    text: QuestionTextType;
    type: typeof zodQuestionTypes | string;
    values?: string[];

    flow: AnswerOptions;
};

export type QuestionnaireFlowType = {
    id: string;
    name: string;
    questions: Question[];
}

export type StepAction =
    | { type: "complete"; index: number }
    | { type: "skip"; index: number };

/**
 *   Schemas
 **/

// Defines the flow schema (form of the individual question response options)
export const FlowSchema = z.object({
    yes: z.string().optional(),
    no: z.string().optional(),
    na: z.string().optional(),
    dontKnow: z.string().optional(),
    next: z.string().optional(),
});

//Defines the question schema (form of question)
export const QuestionSchema = z.object({
    id: z.string(),
    text: z.string(),
    // type: z.nativeEnum(QuestionTypeEnum),
    type: z.string(), // Can extend with more question types if needed. Will need to update this and the QuestionField component if needed.
    values: z.array(z.string()).optional(),
    flow: FlowSchema,
});

//Defines the response schema (response of a question used in tracking responses)
export const ResponseSchema = z.object({
    question: QuestionSchema,
    response: z.string(),
});

// Defines the finding area schema (survey section)
export const FindingAreaSchema = z.object({
    id: z.nativeEnum(FindingAreasEnum),
    name: z.string(),
    questions: z.array(QuestionSchema),
});

// Define the main schema for the survey (overarching survey, contining all finding areas)
export const SurveySchema = z.object({
    version: z.string(),
    findingAreas: z.array(FindingAreaSchema),
});

// Export the types of each schema
export type FlowSchemaType = z.infer<typeof FlowSchema>;
export type QuestionSchemaType = z.infer<typeof QuestionSchema>;
export type ResponseSchemaType = z.infer<typeof ResponseSchema>;
export type FindingAreaSchemaType = z.infer<typeof FindingAreaSchema>;
export type SurveySchemaType = z.infer<typeof SurveySchema>

/**
 *   Constants
 **/
export const SupportEmail = "ETRA@alvarezandmarsal.com";
